/**
 * Iris knowledge sources — RAG documents (pasted text or scraped URLs) that get
 * chunked + embedded into the tenant's vector store so Iris can retrieve them at
 * answer time. Distinct from the free-form `{{knowledgeBase}}` entries (see
 * ./knowledge.ts): sources are large/long-form and semantically searched, not
 * inlined into every prompt. Proxied through operations at
 * `/api/messaging/iris/knowledge-sources` (operations is the FE's auth boundary
 * + RBAC gate; it forwards to ai-operations).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/knowledge-sources';

/** How a source's content was supplied. TEXT = pasted content; URL = scraped. */
export const KNOWLEDGE_SOURCE_TYPES = ['TEXT', 'URL'] as const;
export type KnowledgeSourceType = (typeof KNOWLEDGE_SOURCE_TYPES)[number];

/** Ingestion lifecycle: queued → embedding → searchable (or failed). */
export type KnowledgeSourceStatus = 'PENDING' | 'INDEXING' | 'READY' | 'FAILED';

/**
 * Post-ingestion LLM quality verdict. 'good' = clean useful content; 'mixed' =
 * useful but diluted by boilerplate; 'poor' = mostly nav/cart/junk. Null fields
 * mean the source hasn't been evaluated yet (eval runs at the end of indexing).
 */
export type KnowledgeQualityVerdict = 'good' | 'mixed' | 'poor';

export interface KnowledgeSource {
  id: string;
  type: KnowledgeSourceType;
  title: string;
  /** Source URL when `type === 'URL'`; null for pasted text. */
  url: string | null;
  status: KnowledgeSourceStatus;
  /** How many embedded chunks this source produced (0 until READY). */
  chunkCount: number;
  /** Set when `status === 'FAILED'`. */
  error: string | null;
  /** 0–100 usefulness score; null until the quality eval has run. */
  qualityScore: number | null;
  /** Quality bucket; null until evaluated. */
  qualityVerdict: KnowledgeQualityVerdict | null;
  createdAt: string;
  updatedAt: string;
}

/** A single indexed chunk as stored in the vector store. */
export interface KnowledgeSourceChunk {
  index: number;
  text: string;
}

/** Full quality verdict returned by the preview endpoint. */
export interface KnowledgeSourceQuality {
  score: number | null;
  verdict: KnowledgeQualityVerdict | null;
  summary: string | null;
  issues: string[];
  evaluatedAt: string | null;
}

/** Operator preview: source meta + quality verdict + the indexed chunks. */
export interface KnowledgeSourcePreview {
  source: {
    id: string;
    title: string;
    type: KnowledgeSourceType;
    url: string | null;
    status: KnowledgeSourceStatus;
    chunkCount: number;
  };
  quality: KnowledgeSourceQuality;
  chunks: KnowledgeSourceChunk[];
}

export interface CreateKnowledgeSourcePayload {
  type: KnowledgeSourceType;
  title: string;
  /** Required when `type === 'URL'`. */
  url?: string;
  /** Required when `type === 'TEXT'`. */
  content?: string;
}

export const knowledgeSourcesKey = ['messaging', 'iris-knowledge-sources'] as const;

export function useKnowledgeSources() {
  return useQuery({
    queryKey: knowledgeSourcesKey,
    queryFn: () => apiGet<KnowledgeSource[]>(operationsClient, BASE),
    staleTime: 15_000,
    // Sources ingest asynchronously — poll while anything is still processing.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((s) => s.status === 'PENDING' || s.status === 'INDEXING')
        ? 4_000
        : false,
  });
}

/**
 * Fetch a source's quality verdict + indexed chunk texts for the preview
 * dialog. Only runs when `enabled` (i.e. the dialog is open) so we don't scroll
 * the vector store for every row. Data is short-lived — chunks change on
 * reindex — but stable enough to cache briefly while the dialog is open.
 */
export function useKnowledgeSourceChunks(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...knowledgeSourcesKey, 'preview', id] as const,
    queryFn: () =>
      apiGet<KnowledgeSourcePreview>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id ?? '')}/chunks`,
      ),
    enabled: enabled && !!id,
    staleTime: 30_000,
  });
}

export function useCreateKnowledgeSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateKnowledgeSourcePayload) =>
      apiPost<KnowledgeSource, CreateKnowledgeSourcePayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: knowledgeSourcesKey });
    },
  });
}

export function useDeleteKnowledgeSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: knowledgeSourcesKey });
    },
  });
}

/** Re-chunk + re-embed every source (e.g. after changing the embedding model or
 * to recover from a partial failure). Fire-and-forget: rows flip back to
 * INDEXING and the list poll picks up the new status. */
export function useReindexKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<{ reindexed: number }, Record<string, never>>(
        operationsClient,
        `${BASE}/reindex`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: knowledgeSourcesKey });
    },
  });
}
